import React, { useEffect, useState } from 'react';
import Header from "../Header";
import Sidebar from "../SideBar";
import Content from '../Content';
import Logo from "../../components/assets/EABC-LOGO-PNG-1-600x605-1-400x403.png";
import authStorage from '../../utilities/Storage';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { FiUser, FiMail, FiPhone, FiMapPin, FiHome, FiBriefcase, FiEdit2, FiSave, FiX, FiUpload, FiCheck, FiAlertCircle, FiGrid, FiList, FiPlus, FiTrash2, FiCamera, FiCalendar, FiShield, FiBox, FiTool } from 'react-icons/fi';
import { Modal, Button, Form } from 'react-bootstrap';

export default function UserProfile() {
  const [email, setEmail] = useState(null);
  const [company_name, setCompany_Name] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({});
  const [activeTab, setActiveTab] = useState('personal');
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending'); // 'pending', 'verified', 'rejected'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [companyIdPreview, setCompanyIdPreview] = useState(null);
  const [additionalDocsPreviews, setAdditionalDocsPreviews] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list' for products/services
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingService, setEditingService] = useState(null);

  // Form validation schema for verification
  const verificationSchema = Yup.object().shape({
    companyId: Yup.mixed().required('Company ID is required'),
    additionalDocs: Yup.mixed(),
    registrationNumber: Yup.string().required('Registration number is required')
  });

  // Form validation schema for profile editing
  const profileSchema = Yup.object().shape({
    full_name: Yup.string().required('Full name is required'),
    phone: Yup.string().required('Phone number is required'),
    country: Yup.string().required('Country is required'),
    organization: Yup.string().required('Organization is required'),
    position: Yup.string().required('Position is required'),
    company_name: Yup.string().required('Company name is required'),
    company_email: Yup.string().email('Invalid email').required('Company email is required'),
    company_phone: Yup.string().required('Company phone is required'),
    company_address: Yup.string().required('Company address is required'),
    company_website: Yup.string().url('Invalid URL'),
    company_sector: Yup.string().required('Company sector is required'),
    company_profile: Yup.string(),
    social_media: Yup.string(),
    participation_type: Yup.string(),
    exhibit_booth: Yup.string(),
    b2b_interest: Yup.string(),
    b2b_details: Yup.string(),
    sponsorship_interest: Yup.string(),
    need_invoice: Yup.string(),
    comments: Yup.string()
  });

  // Product form validation schema
  const productSchema = Yup.object().shape({
    product_name: Yup.string().required('Product name is required'),
    selling_price: Yup.number().required('Price is required').positive('Price must be positive'),
    description: Yup.string().required('Description is required')
  });

  // Service form validation schema
  const serviceSchema = Yup.object().shape({
    service_name: Yup.string().required('Service name is required'),
    service_description: Yup.string().required('Description is required')
  });

  // Formik form handling for verification
  const verificationFormik = useFormik({
    initialValues: {
      companyId: null,
      additionalDocs: [],
      registrationNumber: ''
    },
    validationSchema: verificationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append('companyId', values.companyId);
        values.additionalDocs.forEach(file => {
          formData.append('additionalDocs', file);
        });
        formData.append('registrationNumber', values.registrationNumber);
        formData.append('email', email);
        formData.append('company_name', company_name);

        const response = await axios.post(
          'https://backend-livestream-production.up.railway.app/api/upload-verification-docs',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(progress);
            }
          }
        );

        if (response.data.ok === 1) {
          setVerificationStatus('pending');
          alert('Documents submitted successfully! Your verification is under review.');
        }
      } catch (error) {
        console.error('Error uploading documents:', error);
        alert('Failed to upload documents. Please try again.');
      } finally {
        setUploadProgress(0);
      }
    }
  });

  // Formik form handling for profile editing
  const profileFormik = useFormik({
    initialValues: {
      full_name: '',
      phone: '',
      country: '',
      organization: '',
      position: '',
      company_name: '',
      company_email: '',
      company_phone: '',
      company_address: '',
      company_website: '',
      company_sector: '',
      company_profile: '',
      social_media: '',
      participation_type: '',
      exhibit_booth: '',
      b2b_interest: '',
      b2b_details: '',
      sponsorship_interest: '',
      need_invoice: '',
      comments: ''
    },
    validationSchema: profileSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        
        // Add all form fields
        Object.keys(values).forEach(key => {
          formData.append(key, values[key]);
        });
        
        // Add email and company_name
        formData.append('email', email);
        formData.append('company_name', company_name);
        
        // Add logo file if exists
        if (logoFile) {
          formData.append('logo', logoFile);
        }

        const response = await axios.post(
          'https://backend-livestream-production.up.railway.app/api/add-personal-detail',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data.ok === 1) {
          setProfileData(response.data.data);
          setIsEditing(false);
          alert('Profile updated successfully!');
          // Reload profile data to get updated logo
          loadPersonalDetails(email, company_name);
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update profile. Please try again.');
      }
    }
  });

  // Formik form handling for product
  const productFormik = useFormik({
    initialValues: {
      product_name: '',
      selling_price: '',
      description: ''
    },
    validationSchema: productSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await axios.post(
          'https://backend-livestream-production.up.railway.app/api/add-products',
          {
            ...values,
            email: email,
            company_name: company_name
          }
        );

        if (response.data.ok === 1) {
          loadProducts(email, company_name);
          setShowProductModal(false);
          resetForm();
          setEditingProduct(null);
          alert('Product added successfully!');
        }
      } catch (error) {
        console.error('Error adding product:', error);
        alert('Failed to add product. Please try again.');
      }
    }
  });

  // Formik form handling for service
  const serviceFormik = useFormik({
    initialValues: {
      service_name: '',
      service_description: ''
    },
    validationSchema: serviceSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await axios.post(
          'https://backend-livestream-production.up.railway.app/api/add-service',
          {
            ...values,
            email: email,
            company_name: company_name
          }
        );

        if (response.data.ok === 1) {
          loadServices(email, company_name);
          setShowServiceModal(false);
          resetForm();
          setEditingService(null);
          alert('Service added successfully!');
        }
      } catch (error) {
        console.error('Error adding service:', error);
        alert('Failed to add service. Please try again.');
      }
    }
  });

  // Handle file changes for verification
  const handleCompanyIdChange = (event) => {
    const file = event.currentTarget.files[0];
    verificationFormik.setFieldValue('companyId', file);
    
    // Create preview
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCompanyIdPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setCompanyIdPreview(null);
    }
  };

  const handleAdditionalDocsChange = (event) => {
    const files = Array.from(event.currentTarget.files);
    verificationFormik.setFieldValue('additionalDocs', files);
    
    // Create previews
    if (files.length > 0) {
      const previews = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          previews.push(reader.result);
          if (previews.length === files.length) {
            setAdditionalDocsPreviews(previews);
          }
        };
        reader.readAsDataURL(file);
      });
    } else {
      setAdditionalDocsPreviews([]);
    }
  };

  // Handle logo file change
  const handleLogoChange = (event) => {
    const file = event.currentTarget.files[0];
    setLogoFile(file);
    
    // Create preview
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };

  const loadPersonalDetails = async (userEmail, userCompany) => {
    try {
      const response = await axios.get('https://backend-livestream-production.up.railway.app/api/get-personal-detail-by-email-company-name', {
        params: {
          email: userEmail,
          company_name: userCompany
        }
      });
      
      if (response.data.ok === 1 && response.data.data && response.data.data.length > 0) {
        const data = response.data.data[0];
        setProfileData(data);
        
        // Set form values when profile data is loaded
        profileFormik.setValues({
          full_name: data.full_name || '',
          phone: data.phone || '',
          country: data.country || '',
          organization: data.organization || '',
          position: data.position || '',
          company_name: data.company_name || '',
          company_email: data.company_email || '',
          company_phone: data.company_phone || '',
          company_address: data.company_address || '',
          company_website: data.company_website || '',
          company_sector: data.company_sector || '',
          company_profile: data.company_profile || '',
          social_media: data.social_media || '',
          participation_type: data.participation_type || '',
          exhibit_booth: data.exhibit_booth || '',
          b2b_interest: data.b2b_interest || '',
          b2b_details: data.b2b_details || '',
          sponsorship_interest: data.sponsorship_interest || '',
          need_invoice: data.need_invoice || '',
          comments: data.comments || ''
        });
        
        // Set logo preview if logo_url exists - FIXED URL CONSTRUCTION
        if (data.logo_url && data.logo_url !== "no_img") {
          // Remove the leading slash if it exists to avoid double slashes
          const logoPath = data.logo_url.startsWith('/') ? data.logo_url.substring(1) : data.logo_url;
          setLogoPreview(`https://backend-livestream-production.up.railway.app/${logoPath}`);
        }
        
        // Load verification status if available
        if (data.verification_status) {
          setVerificationStatus(data.verification_status);
        }
      }
    } catch (error) {
      console.error('Error loading personal details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async (userEmail, userCompany) => {
    try {
      const response = await axios.get('https://backend-livestream-production.up.railway.app/api/get-products', {
        params: {
          email: userEmail,
          company_name: userCompany
        }
      });
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadServices = async (userEmail, userCompany) => {
    try {
      const response = await axios.get('https://backend-livestream-production.up.railway.app/api/get-services', {
        params: {
          email: userEmail,
          company_name: userCompany
        }
      });
      setServices(response.data.data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const response = await axios.delete(
          `https://backend-livestream-production.up.railway.app/api/delete-product/${productId}`
        );
        if (response.data.ok === 1) {
          loadProducts(email, company_name);
          alert('Product deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Failed to delete product. Please try again.');
      }
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const response = await axios.delete(
          `https://backend-livestream-production.up.railway.app/api/delete-service/${serviceId}`
        );
        if (response.data.ok === 1) {
          loadServices(email, company_name);
          alert('Service deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Failed to delete service. Please try again.');
      }
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    productFormik.setValues({
      product_name: product.product_name,
      selling_price: product.selling_price,
      description: product.description
    });
    setShowProductModal(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    serviceFormik.setValues({
      service_name: service.service_name,
      service_description: service.service_description
    });
    setShowServiceModal(true);
  };

  function getUserRegistrationDetails() {
    const userData = authStorage.getUser();
    if (!userData) return;
    
    try {
      const user = JSON.parse(userData);
      setEmail(user.email);
      setCompany_Name(user.company_name);
      loadPersonalDetails(user.email, user.company_name);
      loadProducts(user.email, user.company_name);
      loadServices(user.email, user.company_name);
    } catch (error) {
      console.error("Failed to parse user data:", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    getUserRegistrationDetails();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleEditProfile = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form to original values
    profileFormik.setValues(profileData);
    // Reset logo preview - FIXED URL CONSTRUCTION
    if (profileData.logo_url && profileData.logo_url !== "no_img") {
      const logoPath = profileData.logo_url.startsWith('/') ? profileData.logo_url.substring(1) : profileData.logo_url;
      setLogoPreview(`https://backend-livestream-production.up.railway.app/${logoPath}`);
    } else {
      setLogoPreview(null);
    }
    setLogoFile(null);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  // Render verification badge based on status
  const renderVerificationBadge = () => {
    switch(verificationStatus) {
      case 'verified':
        return (
          <div className="d-flex align-items-center">
            <FiCheck className="text-success me-1" />
            <span className="badge bg-success">Verified</span>
          </div>
        );
      case 'rejected':
        return (
          <div className="d-flex align-items-center">
            <FiAlertCircle className="text-danger me-1" />
            <span className="badge bg-danger">Rejected</span>
          </div>
        );
      default:
        return (
          <div className="d-flex align-items-center">
            <FiAlertCircle className="text-warning me-1" />
            <span className="badge bg-warning">Pending Verification</span>
          </div>
        );
    }
  };

  const renderField = (icon, label, value) => (
    <div className="info-field">
      <div className="field-icon">
        {icon}
      </div>
      <div className="field-content">
        <div className="field-label">{label}</div>
        <div className="field-value">{value || 'Not provided'}</div>
      </div>
    </div>
  );

  const renderEditableField = (icon, label, name, type = 'text') => (
    <div className="info-field">
      <div className="field-icon">
        {icon}
      </div>
      <div className="field-content flex-grow-1">
        <div className="field-label">{label}</div>
        <input
          type={type}
          className={`form-control ${profileFormik.errors[name] && profileFormik.touched[name] ? 'is-invalid' : ''}`}
          name={name}
          value={profileFormik.values[name] || ''}
          onChange={profileFormik.handleChange}
          onBlur={profileFormik.handleBlur}
        />
        {profileFormik.errors[name] && profileFormik.touched[name] && (
          <div className="invalid-feedback">{profileFormik.errors[name]}</div>
        )}
      </div>
    </div>
  );

  const renderTextareaField = (icon, label, name) => (
    <div className="info-field">
      <div className="field-icon">
        {icon}
      </div>
      <div className="field-content flex-grow-1">
        <div className="field-label">{label}</div>
        <textarea
          className={`form-control ${profileFormik.errors[name] && profileFormik.touched[name] ? 'is-invalid' : ''}`}
          name={name}
          value={profileFormik.values[name] || ''}
          onChange={profileFormik.handleChange}
          onBlur={profileFormik.handleBlur}
          rows="3"
        />
        {profileFormik.errors[name] && profileFormik.touched[name] && (
          <div className="invalid-feedback">{profileFormik.errors[name]}</div>
        )}
      </div>
    </div>
  );

  const tabs = [
    { id: 'personal', label: 'Personal', icon: <FiUser /> },
    { id: 'company', label: 'Company', icon: <FiHome /> },
    { id: 'participation', label: 'Participation', icon: <FiCalendar /> },
    { id: 'products', label: 'Products', icon: <FiBox /> },
    { id: 'services', label: 'Services', icon: <FiTool /> },
    { id: 'verification', label: 'Verification', icon: <FiShield /> }
  ];

  return (
    <div className="app">
      {/* Mobile Header */}
      <div className="d-md-none mobile-header bg-white shadow-sm p-3 d-flex justify-content-between align-items-center">
        <button 
          className="btn btn-outline-secondary"
          onClick={toggleMobileMenu}
        >
          <i className="bi bi-list"></i>
        </button>
        <h5 className="mb-0 fw-bold">Profile</h5>
        <div className="notification-icon position-relative">
          <i className="bi bi-bell-fill"></i>
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            2
          </span>
        </div>
      </div>

      {/* Sidebar with mobile toggle */}
      <div className={`sidebar-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Sidebar />
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={toggleMobileMenu}
        ></div>
      )}

      <div className="main-content">
        {/* Regular Header (hidden on mobile) */}
        <div className="d-none d-md-block">
          <Header />
        </div>

        <Content>
          <motion.div 
            className="container-fluid py-4"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Profile Header Card */}
            <motion.div variants={itemVariants} className="mb-4">
              <div className="profile-header-card">
                <div className="row g-0">
                  {/* Profile Picture Section */}
                  <div className="col-lg-4 col-md-5 profile-picture-section">
                    <div className="d-flex flex-column align-items-center p-4 h-100">
                      <div className="position-relative mb-4">
                        <div className="profile-avatar-container">
                          {logoPreview ? (
                            <img 
                              src={logoPreview} 
                              alt="Company Logo" 
                              className="profile-avatar"
                              onError={(e) => {
                                console.error('Image failed to load:', e);
                                e.target.src = Logo; // Fallback to default logo
                              }}
                            />
                          ) : (
                            <img 
                              src={Logo} 
                              alt="Profile" 
                              className="profile-avatar"
                            />
                          )}

                          {/* Hidden Upload Input */}
                          {isEditing && (
                            <input
                              type="file"
                              accept="image/*"
                              id="profileUpload"
                              style={{ display: "none" }}
                              onChange={handleLogoChange}
                            />
                          )}

                          {/* Camera Button */}
                          {isEditing && (
                            <label htmlFor="profileUpload" className="avatar-edit-btn">
                              <FiCamera />
                            </label>
                          )}
                        </div>
                      </div>
                      <h3 className="fw-bold text-center mb-2">
                        {profileData.company_name || 'Company Name'}
                      </h3>
                      <p className="text-muted text-center mb-3">{profileData.email || 'Email'}</p>
                      <div className="d-flex flex-column align-items-center gap-2 mb-4">
                        <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                          {profileData.position || 'Position'}
                        </span>
                        {renderVerificationBadge()}
                      </div>
                      {isEditing ? (
                        <div className="d-flex gap-2 w-100">
                          <button 
                            type="button" 
                            className="btn btn-primary flex-grow-1 d-flex align-items-center justify-content-center"
                            onClick={profileFormik.handleSubmit}
                          >
                            <FiSave className="me-2" />Save
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                            onClick={handleCancelEdit}
                          >
                            <FiX />
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                          onClick={handleEditProfile}
                        >
                          <FiEdit2 className="me-2" />Edit Profile
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Profile Stats and Tabs Section */}
                  <div className="col-lg-8 col-md-7">
                    <div className="p-4 h-100 d-flex flex-column">
                      {/* Tab Navigation */}
                      <div className="tab-navigation mb-4">
                        <div className="d-flex flex-wrap">
                          {tabs.map(tab => (
                            <button
                              key={tab.id}
                              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                              onClick={() => handleTabChange(tab.id)}
                            >
                              <span className="tab-icon">{tab.icon}</span>
                              <span className="tab-label">{tab.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tab Content */}
                      <div className="tab-content flex-grow-1">
                        <AnimatePresence mode="wait">
                          {/* Personal Information Tab */}
                          {activeTab === 'personal' && (
                            <motion.div
                              key="personal"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="h-100"
                            >
                              <div className="tab-pane h-100">
                                <div className="d-flex align-items-center mb-4">
                                  <div className="tab-icon-large">
                                    <FiUser />
                                  </div>
                                  <div>
                                    <h4 className="mb-1">Personal Information</h4>
                                    <p className="text-muted mb-0">Manage your personal details</p>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6">
                                    {isEditing ? (
                                      renderEditableField(<FiUser />, 'Full Name', 'full_name')
                                    ) : (
                                      renderField(<FiUser />, 'Full Name', profileData.full_name)
                                    )}
                                    {renderField(<FiMail />, 'Email', profileData.email)}
                                    {isEditing ? (
                                      renderEditableField(<FiPhone />, 'Phone', 'phone')
                                    ) : (
                                      renderField(<FiPhone />, 'Phone', profileData.phone)
                                    )}
                                  </div>
                                  <div className="col-md-6">
                                    {isEditing ? (
                                      renderEditableField(<FiMapPin />, 'Country', 'country')
                                    ) : (
                                      renderField(<FiMapPin />, 'Country', profileData.country)
                                    )}
                                    {isEditing ? (
                                      renderEditableField(<FiHome />, 'Organization', 'organization')
                                    ) : (
                                      renderField(<FiHome />, 'Organization', profileData.organization)
                                    )}
                                    {isEditing ? (
                                      renderEditableField(<FiBriefcase />, 'Position', 'position')
                                    ) : (
                                      renderField(<FiBriefcase />, 'Position', profileData.position)
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}

                          {/* Company Information Tab */}
                          {activeTab === 'company' && (
                            <motion.div
                              key="company"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="h-100"
                            >
                              <div className="tab-pane h-100">
                                <div className="d-flex align-items-center mb-4">
                                  <div className="tab-icon-large">
                                    <FiHome />
                                  </div>
                                  <div>
                                    <h4 className="mb-1">Company Information</h4>
                                    <p className="text-muted mb-0">Manage your company details</p>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6">
                                    {isEditing ? (
                                      renderEditableField(<FiHome />, 'Company Name', 'company_name')
                                    ) : (
                                      renderField(<FiHome />, 'Company Name', profileData.company_name)
                                    )}
                                    {isEditing ? (
                                      renderEditableField(<FiMail />, 'Company Email', 'company_email', 'email')
                                    ) : (
                                      renderField(<FiMail />, 'Company Email', profileData.company_email)
                                    )}
                                    {isEditing ? (
                                      renderEditableField(<FiPhone />, 'Company Phone', 'company_phone')
                                    ) : (
                                      renderField(<FiPhone />, 'Company Phone', profileData.company_phone)
                                    )}
                                  </div>
                                  <div className="col-md-6">
                                    {isEditing ? (
                                      renderEditableField(<FiMapPin />, 'Company Address', 'company_address')
                                    ) : (
                                      renderField(<FiMapPin />, 'Company Address', profileData.company_address)
                                    )}
                                    {isEditing ? (
                                      renderEditableField(<FiBriefcase />, 'Company Sector', 'company_sector')
                                    ) : (
                                      renderField(<FiBriefcase />, 'Company Sector', profileData.company_sector)
                                    )}
                                    {isEditing ? (
                                      renderTextareaField(<FiUser />, 'Company Profile', 'company_profile')
                                    ) : (
                                      renderField(<FiUser />, 'Company Profile', profileData.company_profile)
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}

                          {/* Participation Information Tab */}
                          {activeTab === 'participation' && (
                            <motion.div
                              key="participation"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="h-100"
                            >
                              <div className="tab-pane h-100">
                                <div className="d-flex align-items-center mb-4">
                                  <div className="tab-icon-large">
                                    <FiCalendar />
                                  </div>
                                  <div>
                                    <h4 className="mb-1">Participation Details</h4>
                                    <p className="text-muted mb-0">Manage your event participation</p>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6">
                                    {isEditing ? (
                                      renderEditableField(<FiCalendar />, 'Participation Type', 'participation_type')
                                    ) : (
                                      renderField(<FiCalendar />, 'Participation Type', profileData.participation_type)
                                    )}
                                    {isEditing ? (
                                      renderEditableField(<FiHome />, 'Exhibit Booth', 'exhibit_booth')
                                    ) : (
                                      renderField(<FiHome />, 'Exhibit Booth', profileData.exhibit_booth)
                                    )}
                                  </div>
                                  <div className="col-md-6">
                                    {isEditing ? (
                                      renderTextareaField(<FiUser />, 'B2B Details', 'b2b_details')
                                    ) : (
                                      renderField(<FiUser />, 'B2B Details', profileData.b2b_details)
                                    )}
                                    {isEditing ? (
                                      renderTextareaField(<FiUser />, 'Comments', 'comments')
                                    ) : (
                                      renderField(<FiUser />, 'Comments', profileData.comments)
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}

                          {/* Products Tab */}
                          {activeTab === 'products' && (
                            <motion.div
                              key="products"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="h-100"
                            >
                              <div className="tab-pane h-100">
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                  <div className="d-flex align-items-center">
                                    <div className="tab-icon-large">
                                      <FiBox />
                                    </div>
                                    <div>
                                      <h4 className="mb-1">Products</h4>
                                      <p className="text-muted mb-0">Manage your product listings</p>
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button 
                                      className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                      onClick={() => setViewMode('grid')}
                                    >
                                      <FiGrid />
                                    </button>
                                    <button 
                                      className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                      onClick={() => setViewMode('list')}
                                    >
                                      <FiList />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-primary"
                                      onClick={() => setShowProductModal(true)}
                                    >
                                      <FiPlus />
                                    </button>
                                  </div>
                                </div>

                                {products.length > 0 ? (
                                  <div className={viewMode === 'grid' ? 'row' : ''}>
                                    {products.map((product, index) => (
                                      <motion.div 
                                        key={index} 
                                        className={viewMode === 'grid' ? 'col-md-6 col-lg-4 mb-3' : 'mb-3'}
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 10 }}
                                      >
                                        <div className="card h-100 product-card">
                                          <div className="card-body">
                                            <h5 className="card-title">{product.product_name}</h5>
                                            <p className="card-text text-muted">{product.description}</p>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <span className="badge bg-success">${product.selling_price}</span>
                                              <div>
                                                <button 
                                                  className="btn btn-sm btn-outline-primary me-1"
                                                  onClick={() => handleEditProduct(product)}
                                                >
                                                  <FiEdit2 />
                                                </button>
                                                <button 
                                                  className="btn btn-sm btn-outline-danger"
                                                  onClick={() => handleDeleteProduct(product.id)}
                                                >
                                                  <FiTrash2 />
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </motion.div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="empty-state text-center py-5">
                                    <FiBox className="display-4 text-muted mb-3" />
                                    <h4 className="h5">No products found</h4>
                                    <p className="text-muted">Add your first product to get started</p>
                                    <button className="btn btn-primary mt-2" onClick={() => setShowProductModal(true)}>
                                      <FiPlus className="me-2" />Add Product
                                    </button>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}

                          {/* Services Tab */}
                          {activeTab === 'services' && (
                            <motion.div
                              key="services"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="h-100"
                            >
                              <div className="tab-pane h-100">
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                  <div className="d-flex align-items-center">
                                    <div className="tab-icon-large">
                                      <FiTool />
                                    </div>
                                    <div>
                                      <h4 className="mb-1">Services</h4>
                                      <p className="text-muted mb-0">Manage your service offerings</p>
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button 
                                      className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                      onClick={() => setViewMode('grid')}
                                    >
                                      <FiGrid />
                                    </button>
                                    <button 
                                      className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                      onClick={() => setViewMode('list')}
                                    >
                                      <FiList />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-primary"
                                      onClick={() => setShowServiceModal(true)}
                                    >
                                      <FiPlus />
                                    </button>
                                  </div>
                                </div>

                                {services.length > 0 ? (
                                  <div className={viewMode === 'grid' ? 'row' : ''}>
                                    {services.map((service, index) => (
                                      <motion.div 
                                        key={index} 
                                        className={viewMode === 'grid' ? 'col-md-6 col-lg-4 mb-3' : 'mb-3'}
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 10 }}
                                      >
                                        <div className="card h-100 service-card">
                                          <div className="card-body">
                                            <h5 className="card-title">{service.service_name}</h5>
                                            <p className="card-text text-muted">{service.service_description}</p>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <span className="badge bg-purple text-white">{service.company_name}</span>
                                              <div>
                                                <button 
                                                  className="btn btn-sm btn-outline-primary me-1"
                                                  onClick={() => handleEditService(service)}
                                                >
                                                  <FiEdit2 />
                                                </button>
                                                <button 
                                                  className="btn btn-sm btn-outline-danger"
                                                  onClick={() => handleDeleteService(service.id)}
                                                >
                                                  <FiTrash2 />
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </motion.div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="empty-state text-center py-5">
                                    <FiTool className="display-4 text-muted mb-3" />
                                    <h4 className="h5">No services found</h4>
                                    <p className="text-muted">Add your first service to get started</p>
                                    <button className="btn btn-primary mt-2" onClick={() => setShowServiceModal(true)}>
                                      <FiPlus className="me-2" />Add Service
                                    </button>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}

                          {/* Verification Tab */}
                          {activeTab === 'verification' && (
                            <motion.div
                              key="verification"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="h-100"
                            >
                              <div className="tab-pane h-100">
                                <div className="d-flex align-items-center mb-4">
                                  <div className="tab-icon-large">
                                    <FiShield />
                                  </div>
                                  <div>
                                    <h4 className="mb-1">Company Verification</h4>
                                    <p className="text-muted mb-0">Verify your company to build trust</p>
                                  </div>
                                </div>

                                {verificationStatus === 'verified' ? (
                                  <div className="verification-success text-center py-5">
                                    <div className="verification-icon mb-3">
                                      <FiCheck className="text-success" />
                                    </div>
                                    <h4 className="mb-2">Your company has been verified!</h4>
                                    <p className="text-muted">Thank you for completing the verification process.</p>
                                  </div>
                                ) : (
                                  <form onSubmit={verificationFormik.handleSubmit}>
                                    <div className="verification-form">
                                      <div className="mb-4">
                                        <h5 className="mb-3">Upload your company documents for verification</h5>
                                        <p className="text-muted">
                                          To verify your company, please upload the required documents. 
                                          This helps us ensure the authenticity of all participants.
                                        </p>
                                      </div>

                                      {/* Company ID Upload */}
                                      <div className="mb-4">
                                        <label className="form-label fw-bold">Company ID/Registration Document*</label>
                                        <p className="text-muted small mb-2">
                                          Upload a clear copy of your company registration document or business license.
                                          Accepted formats: PDF, JPG, PNG (Max 5MB)
                                        </p>
                                        <div className="file-upload-container">
                                          <input
                                            type="file"
                                            className="file-input"
                                            id="companyId"
                                            name="companyId"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleCompanyIdChange}
                                            required
                                          />
                                          <label htmlFor="companyId" className="file-upload-label">
                                            <FiUpload className="me-2" />
                                            Choose File
                                          </label>
                                          <span className="file-name">
                                            {verificationFormik.values.companyId?.name || 'No file chosen'}
                                          </span>
                                        </div>
                                        {verificationFormik.errors.companyId && (
                                          <div className="text-danger small mt-1">{verificationFormik.errors.companyId}</div>
                                        )}
                                        {companyIdPreview && (
                                          <div className="mt-3">
                                            <h6 className="small">Preview:</h6>
                                            <img 
                                              src={companyIdPreview} 
                                              alt="Company ID Preview" 
                                              className="img-thumbnail"
                                              style={{ maxHeight: '200px' }}
                                            />
                                          </div>
                                        )}
                                      </div>

                                      {/* Additional Documents */}
                                      <div className="mb-4">
                                        <label className="form-label fw-bold">Additional Supporting Documents</label>
                                        <p className="text-muted small mb-2">
                                          You may upload additional documents that help verify your company 
                                          (e.g., tax registration, VAT certificate, etc.)
                                        </p>
                                        <div className="file-upload-container">
                                          <input
                                            type="file"
                                            className="file-input"
                                            id="additionalDocs"
                                            name="additionalDocs"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleAdditionalDocsChange}
                                            multiple
                                          />
                                          <label htmlFor="additionalDocs" className="file-upload-label">
                                            <FiUpload className="me-2" />
                                            Choose Files
                                          </label>
                                          <span className="file-name">
                                            {verificationFormik.values.additionalDocs?.length > 0 
                                              ? `${verificationFormik.values.additionalDocs.length} files selected` 
                                              : 'No files chosen'}
                                          </span>
                                        </div>
                                        {additionalDocsPreviews.length > 0 && (
                                          <div className="mt-3">
                                            <h6 className="small">Previews:</h6>
                                            <div className="d-flex flex-wrap gap-2">
                                              {additionalDocsPreviews.map((preview, index) => (
                                                <img
                                                  key={index}
                                                  src={preview}
                                                  alt={`Document Preview ${index + 1}`}
                                                  className="img-thumbnail"
                                                  style={{ maxHeight: '150px' }}
                                                />
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Registration Number */}
                                      <div className="mb-4">
                                        <label htmlFor="registrationNumber" className="form-label fw-bold">
                                          Company Registration Number*
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          id="registrationNumber"
                                          name="registrationNumber"
                                          value={verificationFormik.values.registrationNumber}
                                          onChange={verificationFormik.handleChange}
                                          placeholder="Enter your official registration number"
                                          required
                                        />
                                        {verificationFormik.errors.registrationNumber && (
                                          <div className="text-danger small mt-1">{verificationFormik.errors.registrationNumber}</div>
                                        )}
                                      </div>

                                      {/* Upload Progress */}
                                      {uploadProgress > 0 && uploadProgress < 100 && (
                                        <div className="mb-3">
                                          <div className="progress">
                                            <div
                                              className="progress-bar progress-bar-striped progress-bar-animated"
                                              role="progressbar"
                                              style={{ width: `${uploadProgress}%` }}
                                              aria-valuenow={uploadProgress}
                                              aria-valuemin="0"
                                              aria-valuemax="100"
                                            >
                                              {uploadProgress}%
                                            </div>
                                          </div>
                                          <div className="text-center small mt-1">
                                            Uploading documents, please wait...
                                          </div>
                                        </div>
                                      )}

                                      {/* Submit Button */}
                                      <div className="d-flex justify-content-end">
                                        <button
                                          type="submit"
                                          className="btn btn-primary"
                                          disabled={uploadProgress > 0 && uploadProgress < 100}
                                        >
                                          {uploadProgress > 0 && uploadProgress < 100 ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                              Uploading...
                                            </>
                                          ) : (
                                            <>
                                              <FiUpload className="me-2" />
                                              Submit for Verification
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  </form>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Content>
      </div>

      {/* Product Modal */}
      <Modal 
        show={showProductModal} 
        onHide={() => {
          setShowProductModal(false);
          setEditingProduct(null);
          productFormik.resetForm();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={productFormik.handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Product Name *</Form.Label>
              <Form.Control
                type="text"
                name="product_name"
                value={productFormik.values.product_name}
                onChange={productFormik.handleChange}
                onBlur={productFormik.handleBlur}
                isInvalid={productFormik.errors.product_name && productFormik.touched.product_name}
                placeholder="Enter product name"
              />
              <Form.Control.Feedback type="invalid">
                {productFormik.errors.product_name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Selling Price *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="selling_price"
                value={productFormik.values.selling_price}
                onChange={productFormik.handleChange}
                onBlur={productFormik.handleBlur}
                isInvalid={productFormik.errors.selling_price && productFormik.touched.selling_price}
                placeholder="Enter price"
              />
              <Form.Control.Feedback type="invalid">
                {productFormik.errors.selling_price}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={productFormik.values.description}
                onChange={productFormik.handleChange}
                onBlur={productFormik.handleBlur}
                isInvalid={productFormik.errors.description && productFormik.touched.description}
                placeholder="Enter product description"
              />
              <Form.Control.Feedback type="invalid">
                {productFormik.errors.description}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="text-muted small">
              <p className="mb-1">
                <strong>Email:</strong> {email}
              </p>
              <p className="mb-0">
                <strong>Company:</strong> {company_name}
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowProductModal(false);
                setEditingProduct(null);
                productFormik.resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingProduct ? 'Update Product' : 'Add Product'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Service Modal */}
      <Modal 
        show={showServiceModal} 
        onHide={() => {
          setShowServiceModal(false);
          setEditingService(null);
          serviceFormik.resetForm();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingService ? 'Edit Service' : 'Add New Service'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={serviceFormik.handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Service Name *</Form.Label>
              <Form.Control
                type="text"
                name="service_name"
                value={serviceFormik.values.service_name}
                onChange={serviceFormik.handleChange}
                onBlur={serviceFormik.handleBlur}
                isInvalid={serviceFormik.errors.service_name && serviceFormik.touched.service_name}
                placeholder="Enter service name"
              />
              <Form.Control.Feedback type="invalid">
                {serviceFormik.errors.service_name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Service Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="service_description"
                value={serviceFormik.values.service_description}
                onChange={serviceFormik.handleChange}
                onBlur={serviceFormik.handleBlur}
                isInvalid={serviceFormik.errors.service_description && serviceFormik.touched.service_description}
                placeholder="Enter service description"
              />
              <Form.Control.Feedback type="invalid">
                {serviceFormik.errors.service_description}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="text-muted small">
              <p className="mb-1">
                <strong>Email:</strong> {email}
              </p>
              <p className="mb-0">
                <strong>Company:</strong> {company_name}
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowServiceModal(false);
                setEditingService(null);
                serviceFormik.resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingService ? 'Update Service' : 'Add Service'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Mobile Bottom Navigation */}
      <div className="d-md-none mobile-bottom-nav fixed-bottom bg-white shadow-lg">
        <div className="d-flex justify-content-around">
          <button className="btn btn-link text-dark">
            <i className="bi bi-house-door-fill fs-5"></i>
            <small className="d-block">Home</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-calendar-event fs-5"></i>
            <small className="d-block">Calendar</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-camera-video fs-5"></i>
            <small className="d-block">Meetings</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-bell fs-5"></i>
            <small className="d-block">Alerts</small>
          </button>
        </div>
      </div>

      <style jsx>{`
        .app {
          display: flex;
          min-height: 100vh;
          position: relative;
        }
        
        .sidebar-wrapper {
          width: 250px;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        
        .main-content {
          flex-grow: 1;
          padding-bottom: 60px; /* Space for mobile bottom nav */
        }
        
        .mobile-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
        }
        
        .mobile-bottom-nav {
          height: 60px;
          z-index: 1000;
        }
        
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
        }
        
        @media (max-width: 767.98px) {
          .sidebar-wrapper {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 1000;
            transform: translateX(-100%);
          }
          
          .sidebar-wrapper.mobile-open {
            transform: translateX(0);
          }
          
          .main-content {
            margin-left: 0;
          }
        }
        
        /* Profile Header Card Styles */
        .profile-header-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          height: 100%;
        }
        
        .profile-picture-section {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
        }
        
        .profile-avatar-container {
          position: relative;
          width: 150px;
          height: 150px;
        }
        
        .profile-avatar {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid white;
        }
        
        .avatar-edit-btn {
          position: absolute;
          bottom: 5px;
          right: 5px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: white;
          color: #6366f1;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          cursor: pointer;
        }
        
        /* Tab Navigation Styles */
        .tab-navigation {
          border-bottom: 1px solid #e9ecef;
          margin-bottom: 1.5rem;
        }
        
        .tab-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1rem;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          color: #6c757d;
          cursor: pointer;
          transition: all 0.2s;
          margin-right: 0.5rem;
        }
        
        .tab-btn:hover {
          color: #6366f1;
        }
        
        .tab-btn.active {
          color: #6366f1;
          border-bottom-color: #6366f1;
        }
        
        .tab-icon {
          font-size: 1.25rem;
          margin-bottom: 0.25rem;
        }
        
        .tab-label {
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .tab-icon-large {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          margin-right: 1rem;
        }
        
        /* Field Styles */
        .info-field {
          display: flex;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }
        
        .field-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          flex-shrink: 0;
        }
        
        .field-content {
          flex-grow: 1;
        }
        
        .field-label {
          font-size: 0.875rem;
          color: #6c757d;
          margin-bottom: 0.25rem;
        }
        
        .field-value {
          font-weight: 500;
        }
        
        /* Product/Service Card Styles */
        .product-card, .service-card {
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          transition: all 0.2s;
          border: 1px solid #e9ecef;
        }
        
        .product-card:hover, .service-card:hover {
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        
        /* Verification Styles */
        .verification-success {
          padding: 2rem;
        }
        
        .verification-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(25, 135, 84, 0.1);
          color: #198754;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          margin: 0 auto;
        }
        
        .verification-form {
          padding: 1rem;
        }
        
        .file-upload-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .file-input {
          position: absolute;
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }
        
        .file-upload-label {
          display: flex;
          align-items: center;
          padding: 0.5rem 1rem;
          background: #f8f9fa;
          border: 1px solid #ced4da;
          border-radius: 0.25rem 0 0 0.25rem;
          cursor: pointer;
        }
        
        .file-name {
          padding: 0.5rem 1rem;
          background: white;
          border: 1px solid #ced4da;
          border-left: none;
          border-radius: 0 0.25rem 0.25rem 0;
          flex-grow: 1;
          color: #6c757d;
        }
        
        .empty-state {
          padding: 3rem 1rem;
        }
        
        .bg-purple {
          background-color: #6f42c1;
        }
      `}</style>
    </div>
  )
}